import React from "react";
import { Lock } from "lucide-react";
import { useLang } from "../i18n";
import { LegalLayout, LegalSection, LegalNote } from "../components/site/LegalLayout";
import { IMPRINT_PUBLISHED } from "../components/site/Footer";

const CONTACT_EMAIL = "info@procurmo.com";

const de = {
  title: "Impressum",
  intro: "Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG).",
  note: "Hinweis: Einige rechtliche Angaben stehen als Platzhalter [ … ] und werden vor dem offiziellen Launch durch die finalen Unternehmensdaten ersetzt.",
  sections: [
    {
      heading: "Diensteanbieter",
      paragraphs: ["[Firmenname] [Rechtsform]", "[Straße und Hausnummer]", "[PLZ Ort]", "Deutschland"],
    },
    {
      heading: "Vertreten durch",
      paragraphs: ["[Name der vertretungsberechtigten Person / Geschäftsführung]"],
    },
    {
      heading: "Kontakt",
      paragraphs: [`E-Mail: ${CONTACT_EMAIL}`, "Telefon: [optional]"],
    },
    {
      heading: "Registereintrag",
      paragraphs: [
        "Eintragung im Handelsregister.",
        "Registergericht: [Registergericht]",
        "Registernummer: [HRB / Registernummer]",
      ],
    },
    {
      heading: "Umsatzsteuer-Identifikationsnummer",
      paragraphs: ["Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz: [USt-IdNr.]"],
    },
    {
      heading: "Redaktionell verantwortlich",
      paragraphs: ["[Name], [Anschrift wie oben]"],
    },
    {
      heading: "EU-Streitschlichtung",
      paragraphs: [
        "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/",
        "Unsere E-Mail-Adresse finden Sie oben im Impressum.",
      ],
    },
    {
      heading: "Verbraucherstreitbeilegung / Universalschlichtungsstelle",
      paragraphs: [
        "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
      ],
    },
    {
      heading: "Haftung für Inhalte",
      paragraphs: [
        "Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.",
        "Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entsprechender Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.",
      ],
    },
    {
      heading: "Haftung für Links",
      paragraphs: [
        "Unser Angebot enthält ggf. Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.",
        "Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.",
      ],
    },
    {
      heading: "Urheberrecht",
      paragraphs: [
        "Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.",
      ],
    },
  ],
};

const en = {
  title: "Legal Notice (Impressum)",
  intro: "Information pursuant to Section 5 of the German Digital Services Act (DDG).",
  note: "Note: Some legal details are shown as placeholders [ … ] and will be replaced with the final company information before the official launch.",
  sections: [
    {
      heading: "Service provider",
      paragraphs: ["[Company name] [legal form]", "[Street and number]", "[Postal code, City]", "Germany"],
    },
    { heading: "Represented by", paragraphs: ["[Name of authorised representative / management]"] },
    { heading: "Contact", paragraphs: [`Email: ${CONTACT_EMAIL}`, "Phone: [optional]"] },
    {
      heading: "Register entry",
      paragraphs: ["Entry in the commercial register.", "Register court: [Register court]", "Register number: [HRB / register number]"],
    },
    { heading: "VAT identification number", paragraphs: ["VAT ID pursuant to Section 27a of the German VAT Act: [VAT ID]"] },
    { heading: "Responsible for content", paragraphs: ["[Name], [address as above]"] },
    {
      heading: "EU dispute resolution",
      paragraphs: [
        "The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr/",
        "Our email address can be found above in this legal notice.",
      ],
    },
    {
      heading: "Consumer dispute resolution",
      paragraphs: ["We are neither willing nor obliged to participate in dispute resolution proceedings before a consumer arbitration board."],
    },
    {
      heading: "Liability for content",
      paragraphs: [
        "As a service provider, we are responsible for our own content on these pages in accordance with general laws pursuant to Section 7(1) DDG. However, pursuant to Sections 8 to 10 DDG, we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.",
        "Obligations to remove or block the use of information under general laws remain unaffected. However, liability in this respect is only possible from the point in time at which knowledge of a specific infringement of the law is obtained. Upon becoming aware of such legal violations, we will remove this content immediately.",
      ],
    },
    {
      heading: "Liability for links",
      paragraphs: [
        "Our website may contain links to external third-party websites over whose content we have no influence. Therefore, we cannot accept any liability for this third-party content. The respective provider or operator of the linked pages is always responsible for their content.",
        "Permanent monitoring of the content of the linked pages is not reasonable without concrete evidence of a legal violation. Upon becoming aware of legal violations, we will remove such links immediately.",
      ],
    },
    {
      heading: "Copyright",
      paragraphs: [
        "The content and works created by the site operators on these pages are subject to German copyright law. Reproduction, editing, distribution and any kind of use outside the limits of copyright law require the written consent of the respective author or creator.",
      ],
    },
  ],
};

export default function Impressum() {
  const { lang } = useLang();
  const c = lang === "de" ? de : en;

  // Frozen state: visible entry, but content not yet published.
  if (!IMPRINT_PUBLISHED) {
    const frozen = lang === "de"
      ? {
          title: "Impressum",
          heading: "In Kürze verfügbar",
          body: "Das Impressum wird derzeit finalisiert und vor dem offiziellen Launch veröffentlicht.",
          contact: `Bei Fragen erreichen Sie uns unter ${CONTACT_EMAIL}.`,
        }
      : {
          title: "Legal Notice (Impressum)",
          heading: "Available soon",
          body: "The legal notice is currently being finalised and will be published before the official launch.",
          contact: `For any questions, please contact us at ${CONTACT_EMAIL}.`,
        };
    return (
      <LegalLayout title={frozen.title}>
        <div
          data-testid="imprint-frozen-notice"
          className="flex flex-col items-center gap-4 rounded-2xl border border-[var(--border)] bg-white px-6 py-14 text-center shadow-md-soft"
        >
          <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--blue-soft)] text-[var(--blue)]">
            <Lock className="h-5 w-5" strokeWidth={1.8} />
          </span>
          <h2 className="font-heading text-xl font-semibold text-[var(--ink)]">{frozen.heading}</h2>
          <p className="max-w-md text-[15px] leading-relaxed text-[var(--slate)]">{frozen.body}</p>
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm font-medium text-[var(--blue)] hover:underline">
            {CONTACT_EMAIL}
          </a>
        </div>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title={c.title} intro={c.intro}>
      <LegalNote>{c.note}</LegalNote>
      {c.sections.map((s) => (
        <LegalSection key={s.heading} heading={s.heading} paragraphs={s.paragraphs} />
      ))}
    </LegalLayout>
  );
}
